/**
 * Browser AHP relay connection runtime.
 *
 * Owns one `AhpClient` + Web PubSub transport pair for a single Mission Control
 * mediated connection to an environment. It connects, discovers the session to
 * observe, applies the subscription snapshot once, then fans every live action
 * out to the caller. Transport drops trigger a token-refreshed re-establish;
 * the snapshot re-applied on reconnect recovers any missed actions (the
 * consumer's projector is idempotent).
 *
 * v1 is observe-only: no turn dispatch or permission responses are sent.
 */

import {AhpClient, type AhpTransport, type Subscription} from '@microsoft/agent-host-protocol/client'
import type {ActionEnvelope, ActionType, SessionState, StateAction, URI} from '@microsoft/agent-host-protocol'
import {WpsTransport, type WebSocketFactory} from './webpubsub/wps-transport'
import {parseGroupName} from './webpubsub/groups'
import {
  buildWpsUrl,
  connectToEnvironment,
  reconnectToEnvironment,
  type MCConnectResponse,
  type MCFetcher,
} from './mission-control/mission-control-environment-client'

const ROOT_CHANNEL = 'ahp-root://' as const
const ACTION_TURN_STARTED = 'session/turnStarted' as ActionType.SessionTurnStarted
const PROTOCOL_VERSIONS = ['0.2.0', '0.1.0'] as const
const RECONNECT_BASE_DELAY_MS = 1_000
const RECONNECT_MAX_DELAY_MS = 30_000

/** The AHP channel for a session, observed over the relay. */
export function sessionChannel(sessionId: string): URI {
  return `ahp-session:/${sessionId}`
}

export type RelayStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed' | 'error'

export interface RelayLogger {
  info(message: string): void
  error(message: string): void
}

const noopLogger: RelayLogger = {info: () => {}, error: () => {}}

export type AhpClientFactory = (transport: AhpTransport) => AhpClient

const defaultAhpClientFactory: AhpClientFactory = transport => new AhpClient(transport)

export interface RelayConnectionRuntimeOptions {
  environmentId: string
  /** Mission Control session id; the relay observes this session's channel. */
  sessionId: string
  /** Channel URI to observe. Defaults to `ahp-session:/<sessionId>`. */
  channel?: URI
  /** Receives the session snapshot each time it is (re)established. */
  onSnapshot: (state: SessionState) => void
  /** Receives every live action in `serverSeq` order. */
  onAction: (env: ActionEnvelope) => void
  onStatus?: (status: RelayStatus, error?: string) => void
  /** Performs Mission Control requests. Required unless both `mcConnect` and `mcReconnect` are supplied. */
  fetcher?: MCFetcher
  /** Mints WPS credentials. Defaults to the Mission Control connect endpoint. */
  mcConnect?: () => Promise<MCConnectResponse>
  /** Refreshes WPS credentials for reconnect. Defaults to the reconnect endpoint. */
  mcReconnect?: (clientId: string) => Promise<MCConnectResponse>
  webSocketFactory?: WebSocketFactory
  ahpClientFactory?: AhpClientFactory
  logger?: RelayLogger
}

function isSessionState(state: unknown): state is SessionState {
  return !!state && typeof state === 'object' && Array.isArray((state as {turns?: unknown}).turns)
}

export class RelayConnectionRuntime {
  private readonly logger: RelayLogger
  private readonly ahpClientFactory: AhpClientFactory

  private client: AhpClient | null = null
  private transport: AhpTransport | null = null
  private mcClientId: string | undefined
  private lastSeenServerSeq = 0

  /** Bumped on every connect attempt / teardown to invalidate stale async work. */
  private connectGen = 0
  private disposed = false
  private reconnectAttempts = 0

  private readonly options: RelayConnectionRuntimeOptions

  constructor(options: RelayConnectionRuntimeOptions) {
    this.options = options
    this.logger = options.logger ?? noopLogger
    this.ahpClientFactory = options.ahpClientFactory ?? defaultAhpClientFactory
  }

  private observedChannel(): URI {
    return this.options.channel ?? sessionChannel(this.options.sessionId)
  }

  async connect(): Promise<void> {
    if (this.disposed) throw new Error('relay connection runtime is disposed')
    const gen = ++this.connectGen
    this.setStatus('connecting')
    try {
      await this.establish(gen, false)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      const msg = formatError(err)
      this.logger.error(`relay connect failed: ${msg}`)
      this.setStatus('error', msg)
      this.scheduleReconnect(gen)
    }
  }

  /** Dispatch a new user turn on the observed channel. Requires a live connection. */
  dispatchTurn(text: string): void {
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    const action: StateAction = {type: ACTION_TURN_STARTED, turnId: crypto.randomUUID(), userMessage: {text}}
    client.dispatch(this.observedChannel(), action)
  }

  /**
   * Force the runtime to drop its current connection and re-establish, which
   * causes the next `subscribe` to return a fresh snapshot. Useful when a new
   * consumer mounts on top of an already-active runtime whose cached snapshot
   * may not reflect the current daemon state (e.g. responses streamed in
   * after the original consumer was torn down).
   */
  async refresh(): Promise<void> {
    if (this.disposed) return
    await this.teardownClient()
    const gen = ++this.connectGen
    this.setStatus('connecting')
    try {
      await this.establish(gen, true)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      const msg = formatError(err)
      this.logger.error(`relay refresh failed: ${msg}`)
      this.setStatus('error', msg)
      this.scheduleReconnect(gen)
    }
  }

  async dispose(): Promise<void> {
    if (this.disposed) return
    this.disposed = true
    this.connectGen++
    await this.teardown()
    this.setStatus('closed')
  }

  private async establish(gen: number, isReconnect: boolean): Promise<void> {
    const mc =
      isReconnect && this.mcClientId
        ? await (this.options.mcReconnect?.(this.mcClientId) ?? this.mcReconnectDefault(this.mcClientId))
        : await (this.options.mcConnect?.() ?? this.mcConnectDefault())
    if (gen !== this.connectGen || this.disposed) return
    this.mcClientId = mc.client_id

    const transport = await this.openTransport(mc)
    if (gen !== this.connectGen || this.disposed) {
      await safeClose(transport)
      return
    }
    this.transport = transport

    const client = this.ahpClientFactory(transport)
    this.client = client
    client.connect()

    const init = await client.initialize({
      clientId: mc.client_id,
      protocolVersions: [...PROTOCOL_VERSIONS],
      initialSubscriptions: [ROOT_CHANNEL],
    })
    if (gen !== this.connectGen || this.disposed) return
    this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, init.serverSeq ?? 0)

    const {result, subscription} = await client.subscribe(this.observedChannel())
    if (gen !== this.connectGen || this.disposed) return
    if (result.snapshot) {
      this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, result.snapshot.fromSeq ?? 0)
      if (isSessionState(result.snapshot.state)) {
        this.options.onSnapshot(result.snapshot.state)
      }
    }
    this.reconnectAttempts = 0
    this.setStatus('connected')
    void this.consume(subscription, gen)
  }

  private mcConnectDefault(): Promise<MCConnectResponse> {
    if (!this.options.fetcher) throw new Error('relay connection runtime requires a fetcher or mcConnect')
    return connectToEnvironment(this.options.environmentId, this.options.sessionId, this.options.fetcher)
  }

  private mcReconnectDefault(clientId: string): Promise<MCConnectResponse> {
    if (!this.options.fetcher) throw new Error('relay connection runtime requires a fetcher or mcReconnect')
    return reconnectToEnvironment(this.options.environmentId, clientId, this.options.fetcher)
  }

  private async openTransport(mc: MCConnectResponse): Promise<AhpTransport> {
    const minted = parseGroupName(mc.groups.broadcast)
    return WpsTransport.connect({
      url: buildWpsUrl(mc),
      toHostGroup: mc.groups.to_host,
      joinGroups: [mc.groups.broadcast, mc.groups.to_client],
      groupValidation: {expected: {uid: minted.uid, eid: minted.eid}},
      onProtocolError: err => this.logger.error(`relay WPS frame dropped: ${formatError(err)}`),
      ...(this.options.webSocketFactory ? {webSocketFactory: this.options.webSocketFactory} : {}),
    })
  }

  private async consume(subscription: Subscription, gen: number): Promise<void> {
    try {
      for await (const evt of subscription) {
        if (this.disposed || gen !== this.connectGen) return
        if (evt.type !== 'action') continue
        this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, evt.params.serverSeq ?? 0)
        try {
          this.options.onAction(evt.params)
        } catch (err) {
          this.logger.error(`relay action listener failed: ${formatError(err)}`)
        }
      }
    } catch (err) {
      if (gen === this.connectGen && !this.disposed) {
        this.logger.error(`relay subscription ended: ${formatError(err)}`)
      }
    }
    if (gen === this.connectGen && !this.disposed) {
      this.scheduleReconnect(gen)
    }
  }

  private scheduleReconnect(gen: number): void {
    if (this.disposed || gen !== this.connectGen) return
    const attempt = this.reconnectAttempts++
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS)
    this.setStatus('reconnecting')
    setTimeout(() => {
      if (this.disposed || gen !== this.connectGen) return
      const reconnectGen = ++this.connectGen
      void this.reestablish(reconnectGen)
    }, delay)
  }

  private async reestablish(gen: number): Promise<void> {
    await this.teardownClient()
    try {
      await this.establish(gen, true)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      this.logger.error(`relay reconnect failed: ${formatError(err)}`)
      this.scheduleReconnect(gen)
    }
  }

  private async teardown(): Promise<void> {
    await this.teardownClient()
  }

  private async teardownClient(): Promise<void> {
    const client = this.client
    this.client = null
    if (client) {
      try {
        await client.shutdown()
      } catch {
        // best-effort
      }
    }
    const transport = this.transport
    this.transport = null
    if (transport) await safeClose(transport)
  }

  private setStatus(status: RelayStatus, error?: string): void {
    this.options.onStatus?.(status, error)
  }
}

function formatError(err: unknown): string {
  if (err instanceof Error) return err.message
  return String(err)
}

async function safeClose(transport: AhpTransport): Promise<void> {
  try {
    await transport.close()
  } catch {
    // best-effort
  }
}
